/*
  Load more for a listing (sections/ncf-blog-grid.liquid), the same behavior as the
  collection grid's Load more (ncf-collection-grid.js) without its sorting and groups.
  - The button is a real link to the next page, so it works without JS, and Cmd, Ctrl or
    Shift-click still opens that page in a new tab.
  - With JS, the next page's items come from the Section Rendering API and are added to the
    list in place; the link then points at the following page, or goes away on the last one.
  - Focus moves to the first new item (before the link can be removed), so keyboard and
    screen reader users land on it. The list is aria-busy only while it is changing.
  - If the request fails, the link is followed as a normal page load.
  Markup: <ncf-load-more data-section-id> holding [data-load-items] (the list) and
  [data-more] (the wrapper of the [data-load-more] link).
*/
if (!customElements.get('ncf-load-more')) {
  customElements.define(
    'ncf-load-more',
    class NcfLoadMore extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      sectionUrl(href) {
        const url = new URL(href, window.location.origin);
        url.hash = '';
        url.searchParams.set('section_id', this.dataset.sectionId);
        return url.toString();
      }

      async fetchNext(href) {
        const response = await fetch(this.sectionUrl(href));
        if (!response.ok) throw new Error(`Load more request failed: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = html.querySelector('ncf-load-more');
        if (!next) throw new Error('Load more: list missing from response');
        return next;
      }

      async onClick(event) {
        const link = event.target.closest('[data-load-more]');
        if (!link || !this.contains(link)) return;
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (this.loading) return;

        const list = this.querySelector('[data-load-items]');
        this.loading = true;
        if (list) list.setAttribute('aria-busy', 'true');
        try {
          const next = await this.fetchNext(link.href);
          const items = Array.from(next.querySelectorAll('[data-load-items] > li'));
          const fragment = document.createDocumentFragment();
          items.forEach((item) => fragment.appendChild(item));
          list.appendChild(fragment);
          list.removeAttribute('aria-busy');

          // Move focus only if it is still on the link (or was dropped to the page), and do it
          // before the link can be removed. No new items: the last item stands in.
          const active = document.activeElement;
          if (active === link || active === document.body || !active) {
            const holder = items[0] || list.lastElementChild;
            const target = holder && holder.querySelector('a[href]');
            if (target) target.focus();
          }

          const nextLink = next.querySelector('[data-load-more]');
          if (nextLink) {
            link.setAttribute('href', nextLink.getAttribute('href'));
          } else {
            const more = this.querySelector('[data-more]');
            if (more) more.remove();
          }
        } catch (error) {
          console.error(error);
          window.location.href = link.href;
        } finally {
          if (list) list.removeAttribute('aria-busy');
          this.loading = false;
        }
      }
    }
  );
}
